import { useCallback, useMemo, useState } from "react";
import type { MapTileLayer } from "../utils/types";
import {
  type ChartConfig,
  type MapActualBitrateColoring,
  type PersistedSettings,
  loadPersistedSettings,
  BPSLOG_SETTINGS_STORAGE_KEY,
} from "../utils/settings";
import { persistSettingsMerged } from "../utils/persist";

// bpslog設定（遅延/チャート/地図/動画等）のstate初期化をまとめる。
export const useBpsLogSettingsState = (args: {
  defaultDelayOffsetMs: number;
  defaultCharts: ChartConfig[];
  defaultFollowMap: boolean;
  defaultShowVideo: boolean;
  defaultDisableChartDownsample: boolean;
  defaultDisableMapDownsample: boolean;
  defaultMapTileLayer: MapTileLayer;
  defaultMapActualBitrateColoring: MapActualBitrateColoring;
}) => {
  const {
    defaultDelayOffsetMs,
    defaultCharts,
    defaultFollowMap,
    defaultShowVideo,
    defaultDisableChartDownsample,
    defaultDisableMapDownsample,
    defaultMapTileLayer,
    defaultMapActualBitrateColoring,
  } = args;

  const persisted = useMemo(() => {
    // localStorageから読み取り（失敗時はnull相当のオブジェクトが返る）。
    return loadPersistedSettings({
      storageKey: BPSLOG_SETTINGS_STORAGE_KEY,
      defaultDelayOffsetMs,
      defaultCharts,
      defaultFollowMap,
      defaultShowVideo,
      defaultMapTileLayer,
      defaultMapActualBitrateColoring,
    });
  }, [
    defaultDelayOffsetMs,
    defaultCharts,
    defaultFollowMap,
    defaultShowVideo,
    defaultMapTileLayer,
    defaultMapActualBitrateColoring,
  ]);

  const [delayOffsetMs, setDelayOffsetMs] = useState<number>(() => {
    return typeof persisted.delayOffsetMs === "number" &&
      Number.isFinite(persisted.delayOffsetMs)
      ? persisted.delayOffsetMs
      : defaultDelayOffsetMs;
  });

  const [chartConfigs, setChartConfigs] = useState<ChartConfig[]>(() => {
    return Array.isArray(persisted.charts) && persisted.charts.length
      ? persisted.charts
      : defaultCharts;
  });

  const [followMap, setFollowMap] = useState<boolean>(() => {
    return typeof persisted.followMap === "boolean"
      ? persisted.followMap
      : defaultFollowMap;
  });

  const [showVideo, setShowVideo] = useState<boolean>(() => {
    return typeof persisted.showVideo === "boolean"
      ? persisted.showVideo
      : defaultShowVideo;
  });

  const [disableChartDownsample, setDisableChartDownsample] = useState<boolean>(
    () => {
      return typeof persisted.disableChartDownsample === "boolean"
        ? persisted.disableChartDownsample
        : defaultDisableChartDownsample;
    },
  );

  const [disableMapDownsample, setDisableMapDownsample] = useState<boolean>(
    () => {
      return typeof persisted.disableMapDownsample === "boolean"
        ? persisted.disableMapDownsample
        : defaultDisableMapDownsample;
    },
  );

  const disableUiCap = disableChartDownsample || disableMapDownsample;

  const [mapTileLayer, setMapTileLayer] = useState<MapTileLayer>(() => {
    return persisted.mapTileLayer ?? defaultMapTileLayer;
  });

  const [mapActualBitrateColoring, setMapActualBitrateColoring] =
    useState<MapActualBitrateColoring>(() => {
      return (
        persisted.mapActualBitrateColoring ?? defaultMapActualBitrateColoring
      );
    });

  const applySettings = useCallback(
    (next: PersistedSettings) => {
      setDelayOffsetMs(next.delayOffsetMs);
      setChartConfigs(next.charts);
      setFollowMap(next.followMap);
      setShowVideo(next.showVideo);

      const nextDisableChartDownsample =
        typeof next.disableChartDownsample === "boolean"
          ? next.disableChartDownsample
          : defaultDisableChartDownsample;
      const nextDisableMapDownsample =
        typeof next.disableMapDownsample === "boolean"
          ? next.disableMapDownsample
          : defaultDisableMapDownsample;

      setDisableChartDownsample(nextDisableChartDownsample);
      setDisableMapDownsample(nextDisableMapDownsample);
      setMapTileLayer(next.mapTileLayer);
      setMapActualBitrateColoring(next.mapActualBitrateColoring);

      // Applyした設定を永続化する（失敗してもUIは継続）。
      persistSettingsMerged({
        storageKey: BPSLOG_SETTINGS_STORAGE_KEY,
        next,
      });
    },
    [defaultDisableChartDownsample, defaultDisableMapDownsample],
  );

  return {
    delayOffsetMs,
    setDelayOffsetMs,
    chartConfigs,
    setChartConfigs,
    followMap,
    setFollowMap,
    showVideo,
    setShowVideo,
    disableUiCap,
    disableChartDownsample,
    setDisableChartDownsample,
    disableMapDownsample,
    setDisableMapDownsample,
    mapTileLayer,
    setMapTileLayer,
    mapActualBitrateColoring,
    setMapActualBitrateColoring,
    applySettings,
  };
};
